import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TableHead, TableHeader as ShadcnTableHeader, TableRow } from "@/components/ui/table";
import { TableHeaderProps } from "./types";
import { cn } from "@/lib/utils";

export const TableHeader = <T,>({ columns, sortConfig, onSort }: TableHeaderProps<T>) => {
  const getSortIcon = (columnKey: keyof T) => {
    if (sortConfig.key !== columnKey) return <ChevronsUpDown className="h-3 w-3 ml-1 flex-shrink-0" />;
    if (sortConfig.direction === "asc") return <ChevronUp className="h-3 w-3 ml-1 flex-shrink-0" />;
    if (sortConfig.direction === "desc") return <ChevronDown className="h-3 w-3 ml-1 flex-shrink-0" />;
    return <ChevronsUpDown className="h-3 w-3 ml-1 flex-shrink-0" />;
  };

  const getColumnWidth = (className: string | undefined) => {
    if (!className) return {};

    // Extract width from className
    const widthMatch = className.match(/w-\[([^\]]+)\]/);
    if (widthMatch) {
      const width = widthMatch[1];
      if (width.includes("%")) {
        return { width };
      } else if (width.includes("px")) {
        return { minWidth: width };
      }
    }

    // Extract min-width from className
    const minWidthMatch = className.match(/min-w-\[([^\]]+)\]/);
    if (minWidthMatch) {
      return { minWidth: minWidthMatch[1] };
    }

    return {};
  };

  return (
    <ShadcnTableHeader>
      <TableRow className="border-b-2 border-gray-300">
        {columns.map((column) => (
          <TableHead
            key={String(column.accessorKey)}
            className={cn(
              "bg-white text-sm font-semibold text-gray-900 p-0",
              column.className, // Apply the same width classes as body cells
              column.headerClassName
            )}
            style={getColumnWidth(column.className)}
          >
            {column.sortable ? (
              <Button
                variant="ghost"
                onClick={() => onSort(column.accessorKey)}
                className="h-full w-full p-3 font-semibold hover:bg-transparent hover:text-gray-900 text-left justify-start flex items-start"
              >
                <span className="flex-1 leading-tight pr-2 whitespace-normal break-words">{column.header}</span>
                {getSortIcon(column.accessorKey)}
              </Button>
            ) : (
              <div className="p-3 h-full flex items-center">
                <span className="leading-tight whitespace-normal break-words">{column.header}</span>
              </div>
            )}
          </TableHead>
        ))}
      </TableRow>
    </ShadcnTableHeader>
  );
};
